// Hood.js - Exaustor Bosch, via Home Connect
//
// Ao contrário da loiça, este aparelho aceita comandos: ligar, luz, brilho
// e velocidade de extração.
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Wind, Power, Lightbulb, Activity, AlertCircle } from 'lucide-react';
import { useHomeConnect, valorDe, NIVEIS_EXTRACAO } from '../hooks/useHomeConnect';
import ViewHeader from './ViewHeader';

const LIGADO = 'BSH.Common.EnumType.PowerState.On';
const DESLIGADO = 'BSH.Common.EnumType.PowerState.Off';
const BRILHOS_RAPIDOS = [25, 50, 75, 100];
const BRILHO_MINIMO = 10;

function Hood({ onBack }) {
  const { t } = useTranslation();
  const {
    appliances, loading, error, emEspera, fetchAppliances, definirSetting, definirExtracao,
  } = useHomeConnect('Hood');

  // Enquanto se arrasta o slider mostra-se este valor; só ao largar é que se
  // envia. Mandar um comando por pixel arrastado foi o que nos deu 429.
  const [brilhoEmCurso, setBrilhoEmCurso] = useState(null);

  const exaustor = appliances.find((a) => a.type === 'Hood');
  const estado = valorDe(exaustor, 'BSH.Common.Status.OperationState');
  const alimentacao = valorDe(exaustor, 'BSH.Common.Setting.PowerState', 'settings');
  const luz = valorDe(exaustor, 'Cooking.Common.Setting.Lighting', 'settings');
  const brilho = valorDe(exaustor, 'Cooking.Common.Setting.LightingBrightness', 'settings');
  const extracao = valorDe(exaustor, 'Cooking.Common.Option.Hood.VentingLevel', 'active_program_options');

  const ligado = alimentacao === LIGADO;
  const nivelAtual = NIVEIS_EXTRACAO.indexOf(extracao);
  const brilhoMostrado = brilhoEmCurso ?? brilho ?? BRILHO_MINIMO;
  const bloqueado = loading || emEspera || !exaustor?.connected;

  const definirBrilho = (valor) => definirSetting(
    exaustor.ha_id, 'Cooking.Common.Setting.LightingBrightness', parseInt(valor, 10),
  );

  const largarSlider = (e) => {
    definirBrilho(e.target.value);
    setBrilhoEmCurso(null);
  };

  const niveis = [
    t('exaustor.velocidadeDesligada'),
    t('exaustor.nivel', { n: 1 }),
    t('exaustor.nivel', { n: 2 }),
    t('exaustor.nivel', { n: 3 }),
  ];

  return (
    <div className="app-container">
      <div className="app-content">
        <ViewHeader
          icon={Wind}
          iconClass="icon-heatpump"
          titulo={t('exaustor.titulo')}
          subtitulo={t('exaustor.subtitulo')}
          onBack={onBack}
          onRefresh={fetchAppliances}
          loading={loading}
        />

        {error && (
          <div className="error-banner">
            <AlertCircle size={20} />
            <span>{error}</span>
          </div>
        )}

        {!exaustor ? (
          <div className="loading-container">
            <div className="loading-spinner" />
            <p>{t('exaustor.aCarregar')}</p>
          </div>
        ) : (
          <div className="heatpump-container">
            <div className={`heatpump-status-card ${exaustor.connected ? 'online' : 'offline'}`}>
              <div className="heatpump-status-header">
                <div className="status-indicator">
                  <div className={`status-dot ${exaustor.connected ? 'online' : 'offline'}`} />
                  <span>{exaustor.connected ? t('comum.online') : t('comum.offline')}</span>
                </div>
                <div className={`power-badge ${ligado ? 'on' : 'off'}`}>
                  {ligado ? t('comum.ligadoMaiusc') : t('comum.desligadoMaiusc')}
                </div>
              </div>
            </div>

            <div className="heatpump-controls-card">
              <h3>{t('comum.controlos')}</h3>

              <div className="control-section">
                <div className="control-header">
                  <Power size={20} />
                  <span>{t('comum.alimentacao')}</span>
                </div>
                <button
                  className={`btn-power ${ligado ? 'on' : 'off'}`}
                  onClick={() => definirSetting(
                    exaustor.ha_id, 'BSH.Common.Setting.PowerState', ligado ? DESLIGADO : LIGADO,
                  )}
                  disabled={bloqueado}
                >
                  <Power size={24} />
                  <span>{ligado ? t('comum.desligar') : t('comum.ligar')}</span>
                </button>
              </div>

              <div className="control-section">
                <div className="control-header">
                  <Lightbulb size={20} />
                  <span>{t('exaustor.luz')}</span>
                </div>
                <button
                  className={`btn-power ${luz ? 'on' : 'off'}`}
                  onClick={() => definirSetting(exaustor.ha_id, 'Cooking.Common.Setting.Lighting', !luz)}
                  disabled={bloqueado}
                >
                  <Lightbulb size={24} />
                  <span>{luz ? t('exaustor.desligarLuz') : t('exaustor.ligarLuz')}</span>
                </button>
              </div>

              {luz && (
                <div className="control-section">
                  <div className="control-header">
                    <Lightbulb size={20} />
                    <span>{t('exaustor.brilhoLuz')}</span>
                    <span style={{ marginLeft: 'auto', fontWeight: 600 }}>{brilhoMostrado}%</span>
                  </div>
                  <input
                    type="range"
                    min={BRILHO_MINIMO}
                    max={100}
                    value={brilhoMostrado}
                    onChange={(e) => setBrilhoEmCurso(parseInt(e.target.value, 10))}
                    onMouseUp={largarSlider}
                    onTouchEnd={largarSlider}
                    className="temp-slider"
                    disabled={bloqueado}
                  />
                  <div className="temp-quick-buttons">
                    {BRILHOS_RAPIDOS.map((pct) => (
                      <button
                        key={pct}
                        className={`temp-quick-btn ${brilho === pct ? 'active' : ''}`}
                        onClick={() => definirBrilho(pct)}
                        disabled={bloqueado}
                      >
                        {pct}%
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="control-section">
                <div className="control-header">
                  <Wind size={20} />
                  <span>{t('exaustor.velocidadeExtracao')}</span>
                </div>
                <div className="temp-quick-buttons">
                  {niveis.map((label, nivel) => (
                    <button
                      key={label}
                      className={`temp-quick-btn ${nivelAtual === nivel ? 'active' : ''}`}
                      onClick={() => definirExtracao(exaustor.ha_id, nivel)}
                      disabled={bloqueado}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="heatpump-info-card">
              <h3>{t('comum.informacoes')}</h3>
              <div className="info-grid">
                <div className="info-item">
                  <Activity size={20} />
                  <div>
                    <span className="info-label">{t('comum.estado')}</span>
                    <span className="info-value">
                      {estado
                        ? t(`loica.estados.${estado.split('.').pop()}`, { defaultValue: estado.split('.').pop() })
                        : '—'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Hood;
